import type { ElementType, ReactNode } from "react";
import type { Link as RouterLink } from "react-router";
import { twJoin } from "tailwind-merge";

import { type BoxStyleProps, boxVariants } from "./Box";
import type { DesignSystemAs, DesignSystemElementProps, DesignSystemRef } from "./contracts/dom";

const CARD_DENSITY_CLASSES = {
  flush: undefined,
  default: "gap-space-md p-space-md",
  compact: "gap-space-sm p-card-compact",
  spacious: "gap-space-lg p-space-lg",
} as const;

export type CardSurfaceStyleProps = {
  variant?: "filled" | "outlined";
  borderTone?: NonNullable<BoxStyleProps["border"]>;
  interactive?: boolean;
};

/** Shared card paint for product cards that need to own their root DOM and layout. */
export function cardSurfaceVariants({
  borderTone = "default",
  interactive = false,
  variant = "filled",
}: CardSurfaceStyleProps = {}): string {
  return twJoin(
    boxVariants({
      background: variant === "filled" ? "default" : undefined,
      border: borderTone,
      borderWidth: "hairline",
      radius: "xl",
    }),
    interactive &&
      "cursor-pointer transition-shadow focus-within:shadow-raised focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-offset-2 focus-visible:outline-hidden hocus:shadow-raised",
  );
}

type CardElement =
  | "div"
  | "section"
  | "article"
  | "button"
  | "li"
  | "ul"
  | "ol"
  | "label"
  | "aside"
  | "footer"
  | typeof RouterLink;

type CardRef<TElement extends CardElement> = TElement extends keyof HTMLElementTagNameMap
  ? HTMLElementTagNameMap[TElement]
  : HTMLAnchorElement;

const CARD_SCROLL_CLASSES = {
  content: "overflow-y-auto",
  compact: "h-scroll-area-compact overflow-y-auto",
  bounded: "max-h-scroll-area-compact overflow-y-auto",
} as const;

export type CardProps<TElement extends CardElement = "div"> = {
  children?: ReactNode;
  clip?: boolean;
  density?: keyof typeof CARD_DENSITY_CLASSES;
  minHeight?: "media";
  height?: "full";
  scroll?: keyof typeof CARD_SCROLL_CLASSES;
} & CardSurfaceStyleProps &
  DesignSystemAs<TElement> &
  DesignSystemElementProps<TElement> &
  DesignSystemRef<CardRef<TElement>>;

export function Card<TElement extends CardElement = "div">({
  as,
  borderTone = "default",
  children,
  clip = false,
  density = "default",
  interactive = false,
  height,
  minHeight,
  ref,
  scroll,
  variant = "filled",
  ...nativeProps
}: CardProps<TElement>): ReactNode {
  const Element = (as ?? "div") as ElementType;

  return (
    <Element
      {...nativeProps}
      ref={ref}
      className={twJoin(
        "relative flex w-full flex-col",
        cardSurfaceVariants({ borderTone, interactive, variant }),
        CARD_DENSITY_CLASSES[density],
        clip && "overflow-hidden",
        minHeight === "media" && "min-h-card-media",
        height === "full" && "h-full",
        scroll && CARD_SCROLL_CLASSES[scroll],
        interactive && "text-left",
      )}
    >
      {children}
    </Element>
  );
}

/** Fixed-height centered content for compact loading and transitional card states. */
export function CardCenteredState({ children }: { children: ReactNode }): ReactNode {
  return <div className="flex h-card-centered-state items-center justify-center">{children}</div>;
}
